<template>
	<div class="accessible-manager">
		<slot />
	</div>
</template>

<script>
import { createLogger } from '@/helpers/log'
import router from '@/router'

const log = createLogger('AccessibleManager')

export const ACTIVE_ELEM_CLASS = 'global-active-element'
export const ACTIVE_ELEM_SELECTOR = `.${ACTIVE_ELEM_CLASS}`

export default {
	name: 'AccessibleManager',
	props: {
		sectionOrder: Array,
	},

	provide() {
		return {
			moveToNextSection: this.moveToNextSection,
			moveToPrevSection: this.moveToPrevSection,
			moveToSectionByName: this.moveToSectionByName,
		}
	},

	mounted() {
		this.focusedElement = null
		this._keyboardNav = false
		this._onMousedown = () => {
			this._keyboardNav = false
		}
		this._onKeydown = (e) => {
			if (e.key === 'Tab') this._keyboardNav = true
		}
		document.addEventListener('mousedown', this._onMousedown, true)
		document.addEventListener('keydown', this._onKeydown, true)
		document.addEventListener('focusin', this.onFocusIn)

		this.routerHook = router.afterEach(async (to, from) => {
			if (to.name !== from.name) {
				// Wait for Vue to finish updating reactive props before focusing
				await this.$nextTick()
				// Only auto-focus sections if user was keyboard-navigating;
				// mouse users should not be forced into accessibility mode
				if (this._keyboardNav) {
					this.moveToSectionAtIndex(0)
				}
			}
		})
	},

	beforeUnmount() {
		this.routerHook?.()
		document.removeEventListener('mousedown', this._onMousedown, true)
		document.removeEventListener('keydown', this._onKeydown, true)
		document.removeEventListener('focusin', this.onFocusIn)
	},

	methods: {
		moveToNextSection(fromSection, currCount = 0) {
			if (currCount > 10) return
			const currentIndex = this.sectionOrder.indexOf(fromSection)
			const worked = this.moveToSectionAtIndex(currentIndex + 1)
			if (!worked) {
				this.moveToNextSection(
					this.sectionOrder[currentIndex + 1],
					currCount + 1
				)
			}
		},

		moveToPrevSection(fromSection, currCount = 0) {
			if (currCount > 10) return
			const currentIndex = this.sectionOrder.indexOf(fromSection)
			const worked = this.moveToSectionAtIndex(currentIndex - 1)
			if (!worked) {
				this.moveToPrevSection(
					this.sectionOrder[currentIndex - 1],
					currCount + 1
				)
			}
		},

		moveToSectionByName(section) {
			const currentIndex = this.sectionOrder.indexOf(section)
			this.moveToSectionAtIndex(currentIndex)
		},

		moveToSectionAtIndex(index) {
			if (index >= this.sectionOrder.length) {
				index = 0
			}
			index < 0 && (index = this.sectionOrder.length - 1)
			document.activeElement?.blur?.()
			return this.focusOnSection(this.sectionOrder[index])
		},

		focusOnSection(sectionName) {
			if (!sectionName) return false
			let elem = document.querySelector(`[data-ally-section="${sectionName}"]`)
			if (!elem) return false
			// Enable accessibility mode on the target — focusOnElem bails without it
			const instance = elem.__vnode?.component?.ctx
			if (instance) instance.accessibilityMode = true
			// Primary method: use __focusMe attached directly to DOM element by Accessible
			if (elem.__focusMe) {
				elem.__focusMe()
				return true
			}
			// Fallback: Vue 3 expose via vnode (for backwards compatibility)
			if (elem.__vnode?.ctx?.exposed?.focusMe) {
				elem.__vnode.ctx.exposed.focusMe()
				return true
			}
			return false
		},

		onFocusIn() {
			const { activeElement } = document
			if (this.focusedElement && this.focusedElement !== activeElement) {
				this.focusedElement.classList.remove(ACTIVE_ELEM_CLASS)
			}
			this.focusedElement = activeElement
			this.focusedElement?.classList.add(ACTIVE_ELEM_CLASS)
		},
	},
}
</script>

<style lang="scss" scoped></style>
